import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { CheckSquare2, Send, FileText, Clock, User, Calendar, Eye, AlertTriangle, Upload } from 'lucide-react';
import ResponsiveTooltip from '../ui/ResponsiveTooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';
import { showToast } from '../../utils/toast';
import { handleAPIError } from '../../utils/errorHandler';
import FileUploadModal from '../FileUploadModal';
import CommentModal from '../CommentModal';
import { actionPointAPI } from '../../Services/apiService';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { truncateText } from '../../lib/utils';
import Pagination from '../Pagination';

const MyPendingTasksTable = ({
  tasks = [],
  page,
  pages,
  total,
  pageSize,
  hasNext,
  hasPrev,
  onPageChange,
  onPageSizeChange
}) => {
  const { user } = useAuth();
  const [showactionbuttons, setShowActionButtons] = useState(false);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewCommentOpen, setViewCommentOpen] = useState(false);
  const [viewCommentText, setViewCommentText] = useState('');
  // Track which rejected tasks have had documents re-uploaded
  const [reuploadedTasks, setReuploadedTasks] = useState(new Set());
  // Sorting state
  const [sortColumn, setSortColumn] = useState('pending_days'); // Default sort by pending days
  const [sortDirection, setSortDirection] = useState('asc'); // asc = fewer days first

  // Persist reuploaded tasks state when tasks refresh
  useEffect(() => {
    // When tasks refresh, preserve the reuploaded state for tasks that still exist
    setReuploadedTasks(prev => {
      const newSet = new Set();
      tasks.forEach(task => {
        if (task.status === 'rejected_in_review' && prev.has(task.id)) {
          newSet.add(task.id);
        }
      });
      return newSet;
    });
  }, [tasks]);

  const handleSubmitForReview = (task) => {
    // Check if document is required but not uploaded
    if (task.document_required && !task.document_url) {
      showToast.error('Document upload is required before submission');
      return;
    }

    setSelectedTask(task);
    setCommentModalOpen(true);
  };

  const handleCommentSubmit = async (comment) => {
    if (!selectedTask) return;

    setIsSubmitting(true);
    try {
      await actionPointAPI.submitForReview(selectedTask.id, comment);
      showToast.success('Task submitted for review');

      // Clear reuploaded state if this was a rejected task being resubmitted
      if (selectedTask.status === 'rejected_in_review') {
        setReuploadedTasks(prev => {
          const newSet = new Set(prev);
          newSet.delete(selectedTask.id);
          return newSet;
        });
      }

      // Trigger parent refresh without page reload
      window.dispatchEvent(new CustomEvent('taskStatusChanged'));

      // Close modal and reset on success
      setCommentModalOpen(false);
      setSelectedTask(null);
    } catch (error) {
      handleAPIError(error, 'Failed to submit task', true);
      throw error; // Re-throw to prevent clearing comment
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadDocument = (task) => {
    setSelectedTask(task);
    setFileUploadModalOpen(true);
  };

  const handleFileUploadSuccess = () => {
    setFileUploadModalOpen(false);
    showToast.success('Document uploaded successfully');

    // If this was a rejected task, mark it as re-uploaded
    if (selectedTask && selectedTask.status === 'rejected_in_review') {
      setReuploadedTasks(prev => new Set(prev).add(selectedTask.id));
    }

    // Trigger parent refresh without page reload
    window.dispatchEvent(new CustomEvent('taskStatusChanged'));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500 text-white">Pending</Badge>;
      case 'submitted_for_review':
        return <Badge variant="outline">Submitted for Review</Badge>;
      case 'completed':
        return <Badge variant="outline" className="text-green-600">Completed</Badge>;
      case 'approved':
        return <Badge variant="outline" className="text-green-600">Completed</Badge>; // Backward compatibility
      case 'rejected_in_review':
        return <Badge variant="destructive">Rejected in Review</Badge>;
      default:
        // Convert status to proper title case
        const formattedStatus = status
          ? status
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
          : status;
        return <Badge variant="secondary">{formattedStatus}</Badge>;
    }
  };

  // Fixed overdue logic: Task is overdue only AFTER due date passes
  const isOverdue = (task) => {
    if (!task.due_date) return false;
    const dueDate = new Date(task.due_date);
    dueDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Overdue only if today is AFTER due date (not on due date)
    // Exclude only completed and submitted tasks from overdue check
    return today > dueDate && !['completed', 'approved', 'submitted_for_review'].includes(task.status);
  };

  const getPendingDays = (task) => {
    if (!task.due_date) return null;
    const dueDate = new Date(task.due_date);
    dueDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = dueDate - today;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Handle sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return (
        <span className="ml-1 text-gray-400">
          {sortDirection === 'asc' ? '▲' : '▼'}
        </span>
      );
    }
    return null;
  };

  // Filter out overdue tasks and sort based on selected column
  const filteredAndSortedTasks = tasks
    .filter(task => !isOverdue(task)) // Remove overdue tasks from pending list
    .sort((a, b) => {
      let compareValue = 0;

      if (sortColumn === 'pending_days') {
        const daysA = getPendingDays(a);
        const daysB = getPendingDays(b);
        // Handle null values
        if (daysA === null) return 1;
        if (daysB === null) return -1;
        compareValue = daysA - daysB;
      } else if (sortColumn === 'due_date') {
        const dateA = a.due_date ? new Date(a.due_date).getTime() : 0;
        const dateB = b.due_date ? new Date(b.due_date).getTime() : 0;
        compareValue = dateA - dateB;
      } else if (sortColumn === 'task') {
        const descA = (a.description || '').toLowerCase();
        const descB = (b.description || '').toLowerCase();
        compareValue = descA.localeCompare(descB);
      }

      return sortDirection === 'asc' ? compareValue : -compareValue;
    });

  if (filteredAndSortedTasks.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <CheckSquare2 className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Pending Tasks</h3>
          <p className="text-muted-foreground text-center">
            You don't have any pending tasks at the moment.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>

        <CardContent>
          {/* Desktop Table View */}
          <div className="hidden md:block border rounded-lg mt-2 overflow-x-auto">
            <div className="max-h-[calc(100vh-290px)] overflow-auto">
              <div className="relative w-full overflow-visible">
                <table className="w-full caption-bottom text-sm ">
                  <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm [&_tr]:border-b">
                    <tr className='bg-gray-100 border-b'>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Sr. No.</th>
                      <th
                        className="h-10 px-2 text-left align-left font-medium text-muted-foreground cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('task')}
                      >
                        <div className="flex items-center justify-left  ">
                          Task
                          {getSortIcon('task')}
                        </div>
                      </th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Approver</th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Status</th>
                      <th
                        className="h-10 px-2 text-center align-middle font-medium text-muted-foreground cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('due_date')}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Due Date
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {getSortIcon('due_date')}
                        </div>
                      </th>
                      <th
                        className="h-10 px-2 text-center align-middle font-medium text-muted-foreground cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('pending_days')}
                      >
                        <div className="flex items-center justify-center">
                          Pending Days
                          {getSortIcon('pending_days')}
                        </div>
                      </th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Remark</th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground w-10">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="[&_tr:last-child]:border-0">
                    {filteredAndSortedTasks.map((task, index) => (
                      <tr key={task.id} className="border-b transition-colors hover:bg-gray-50">
                        <td className="p-2 text-center align-middle">
                          {(page - 1) * pageSize + index + 1}
                        </td>
                        <td className="p-2 text-left align-left w-72">
                          <div className="space-y-1">
                            {task.description && task.description.length > 20 ? (
                              <ResponsiveTooltip
                                content={<p className="text-sm whitespace-pre-wrap">{task.description}</p>}
                                className="max-w-md p-3"
                              >
                                <div className="font-medium cursor-pointer mx-auto">
                                  {truncateText(task.description || 'No description', 20)}
                                </div>
                              </ResponsiveTooltip>
                            ) : (
                              <div className="font-medium mx-auto">{task.description || 'No description'}</div>
                            )}
                            {task.meeting?.title && (
                              task.meeting.title.length > 20 ? (
                                <ResponsiveTooltip
                                  content={<p className="max-w-xs whitespace-pre-wrap">{task.meeting.title}</p>}
                                >
                                  <span className="text-sm text-gray-600 cursor-pointer">
                                    Meeting: {truncateText(task.meeting.title, 20)}
                                  </span>
                                </ResponsiveTooltip>
                              ) : (
                                <span className="text-sm text-gray-600">
                                  Meeting: {task.meeting.title}
                                </span>
                              )
                            )}
                          </div>
                        </td>
                        <td className="p-2 text-center align-middle">
                          <div className="text-sm text-gray-900">
                            {task.approver?.full_name || 'Not Assigned'}
                          </div>
                        </td>
                        <td className="p-2 text-center align-middle w-fit">
                          {getStatusBadge(task.status)}
                        </td>
                        <td className="p-2 text-center align-middle">
                          <div>{formatDate(task.due_date)}</div>
                        </td>
                        <td className="p-2 text-center align-middle">
                          {(() => {
                            const pendingDays = getPendingDays(task);
                            if (pendingDays === null) {
                              return <Badge variant="secondary">N/A</Badge>;
                            }
                            if (pendingDays === 0) {
                              return (
                                <Badge variant="destructive">
                                  0
                                </Badge>
                              );
                            } else {
                              return (
                                <div className="flex justify-center">
                                  <span className="text-white bg-[#FFC107] font-medium border w-fit p-2 rounded-lg ">{pendingDays}</span>
                                </div>
                              );
                            }
                          })()}
                        </td>
                        <td className="p-2 text-center align-middle">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setViewCommentText(task.review_comment || 'No comment available');
                              setViewCommentOpen(true);
                            }}
                            disabled={!task.review_comment}
                            className={task.review_comment ? "text-red-600 hover:text-red-700" : "text-gray-400 cursor-not-allowed"}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                          </Button>
                        </td>
                        <td className="p-2 text-center align-middle">
                          <div className="flex items-center justify-center gap-2">
                            {task.status === 'pending' && task.document_required && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUploadDocument(task)}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                {task.document_url ? "Re-upload Doc" : "Upload Doc"}
                              </Button>
                            )}

                            {task.status === 'rejected_in_review' && (
                              <>
                                {task.document_required && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUploadDocument(task)}
                                    className="text-orange-600  hover:text-orange-700"
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    {task.document_url ? "Re-upload Doc" : "Upload Doc"}
                                  </Button>
                                )}

                              </>
                            )}
                            {task.document_url && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(task.document_url, '_blank')}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                View
                              </Button>
                            )}
                            {task.status === 'rejected_in_review' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSubmitForReview(task)}
                                className="text-green-600 hover:text-green-700"
                                disabled={task.document_required && !reuploadedTasks.has(task.id)}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Resubmit
                              </Button>
                            )}
                            {task.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSubmitForReview(task)}
                                className="text-blue-600 hover:text-blue-700"
                                disabled={task.document_required && !task.document_url}
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Submit
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile Accordion View */}
          <div className="md:hidden mt-2">
            <Accordion type="single" collapsible className="w-full space-y-2">
              {filteredAndSortedTasks.map((task, index) => {
                const pendingDays = getPendingDays(task);
                return (
                  <AccordionItem key={task.id} value={`pending-${task.id}`} className="border rounded-lg px-4 bg-white">
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-500 flex-shrink-0">
                            {(page - 1) * pageSize + index + 1}.
                          </span>
                          <div className="flex-1 min-w-0">
                            {task.description && task.description.length > 20 ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <span className="text-sm font-medium text-gray-900 text-left hover:underline cursor-pointer inline-block">
                                    {truncateText(task.description || 'No description', 20)}
                                  </span>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 max-h-[200px] p-3 overflow-y-auto " align="start">
                                  <div className="text-sm font-medium text-gray-900 whitespace-pre-wrap">
                                    {task.description || 'No description'}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <p className="text-sm font-medium text-gray-900">{task.description || 'No description'}</p>
                            )}

                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-4 pt-0">
                      <div className="space-y-4  pt-2 border-t">
                        {task.meeting?.title && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Meeting</p>
                            {task.meeting.title.length > 20 ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <span className="text-sm text-gray-900 text-left hover:underline cursor-pointer inline-block">
                                    {truncateText(task.meeting.title, 20)}
                                  </span>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 p-3" align="start">
                                  <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                    {task.meeting.title}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <p className="text-sm text-gray-900">{task.meeting.title}</p>
                            )}
                          </div>
                        )}
                        <div className='flex flex-wrap justify-between'>
                          <div >
                            <p className="text-xs font-medium text-gray-500 mb-1">Approver</p>
                            <p className="text-sm text-gray-900">{task.approver?.full_name || 'Not Assigned'}</p>
                          </div>

                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Due Date</p>
                            <p className="text-sm text-gray-900">{formatDate(task.due_date)}</p>
                          </div>

                          <div className='flex flex-col justify-center items-center'>
                            <p className="text-xs  font-medium text-gray-500 mb-1">Pending Days</p>
                            {pendingDays === null ? (
                              <Badge variant="secondary" className="text-xs">N/A</Badge>
                            ) : pendingDays === 0 ? (
                              <Badge variant="destructive" className="text-xs">0</Badge>
                            ) : (
                              <span className="text-white bg-[#FFC107] font-medium  border w-fit p-2 rounded-lg text-sm">{pendingDays}</span>
                            )}
                          </div>
                        </div>

                        {task.review_comment && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Remark</p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setViewCommentText(task.review_comment || 'No comment available');
                                setViewCommentOpen(true);
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Remark
                            </Button>
                          </div>
                        )}
                        <div className=''>
                          <p className="text-xs  font-medium text-gray-500 mb-1">Actions </p>
                          <div className="flex flex-wrap  gap-2">
                            {task.status === 'pending' && (
                              <>
                                {task.document_required && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUploadDocument(task)}
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    {task.document_url ? "Re-upload Doc" : "Upload Doc"}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSubmitForReview(task)}
                                  className="text-blue-600 hover:text-blue-700"
                                  disabled={task.document_required && !task.document_url}
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  Submit
                                </Button>
                                {task.document_url && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => window.open(task.document_url, '_blank')}
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    View Doc
                                  </Button>
                                )}


                              </>
                            )}
                            <div >
                              {task.status === 'rejected_in_review' && (
                                <>
                                  {task.document_required && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleUploadDocument(task)}
                                      className="text-orange-600  hover:text-orange-700"
                                    >
                                      <FileText className="h-4 w-4 mr-1" />
                                      {task.document_url ? "Re-upload Doc" : "Upload Doc"}
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleSubmitForReview(task)}
                                    className="text-green-600 hover:text-green-700"
                                    disabled={task.document_required && !reuploadedTasks.has(task.id)}
                                  >
                                    <Send className="h-4 w-4 mr-1" />
                                    Resubmit
                                  </Button>
                                </>
                              )}
                              {task.document_url && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => window.open(task.document_url, '_blank')}
                                >
                                  <FileText className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <Pagination
              page={page}
              pages={pages}
              total={total}
              pageSize={pageSize}
              hasNext={hasNext}
              hasPrev={hasPrev}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      <FileUploadModal
        open={fileUploadModalOpen}
        onClose={() => setFileUploadModalOpen(false)}
        task={selectedTask}
        onUploadSuccess={handleFileUploadSuccess}
      />

      <CommentModal
        open={commentModalOpen}
        onClose={() => setCommentModalOpen(false)}
        onSubmit={handleCommentSubmit}
        title="Submit for Review"
        description="Please provide a remark for submission"
        placeholder="Enter submission remark..."
        submitText="Submit"
        isLoading={isSubmitting}
      />

      <Dialog open={viewCommentOpen} onOpenChange={() => setViewCommentOpen(false)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Remark:</DialogTitle>

          </DialogHeader>
          <div className="whitespace-pre-wrap text-sm text-foreground">
            {viewCommentText}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MyPendingTasksTable;
